import type { BulkResult } from "@/domain/types/bulk";
import type {
  BankAccount,
  BankAccountUpdateRequest,
  BeneficiaryAggregates,
  BeneficiaryAggregatesParams,
  BeneficiaryListFilters,
  BeneficiarySort,
  BeneficiarySortField,
  BeneficiaryStatus,
  LegalRepUpdateRequest,
  NewBankAccount,
} from "@/domain/beneficiary";
import type { IBeneficiaryRepository } from "@/application/ports/repositories";
import { isSubsidyEntry } from "@/domain/beneficiary";
import type { FiscalQuotaResponse } from "../generated/types.gen";
import type {
  BeneficiaryDetail,
  BeneficiaryDetailResponse,
  ConsolidationCommitRequest,
  ConsolidationPreview,
  FiscalQuota,
  FiscalYearSummary,
  CreateFiscalQuotaRequest,
  UpdateFiscalQuotaRequest,
  BeneficiaryMetrics,
  BeneficiaryUpdateRequest,
  CreateBeneficiaryResponse,
  BeneficiaryListParams,
  BeneficiaryExportParams,
  BeneficiaryMetricsParams,
} from "@/domain";
import type {
  DocumentWithReviewResponse,
  DocumentDownloadResponse,
  BeneficiaryPortfolioResponse,
  BeneficiaryDocumentsByFiscalQuotaResponse,
  ReviewUpdateRequest,
  DocumentUpdateRequest,
} from "@/domain/documentBlocks";
import type { ListDocumentsParams } from "@/domain/document";
import type { ExportDownloadOptions, ExportPreview } from "@/domain/export";
import { DocumentType, EntityType } from "@/domain/documentEnums";
import { ApiClientError } from "@/application/errors/ApiClientError";
import { downloadBlob, downloadFromSignedUrl } from "../fileDownload";
import * as sdk from "../generated/sdk.gen";
import { unwrap, unwrapBlobWithFilename } from "../unwrap";

/** subsidies is a JSONB passthrough in the backend — narrow it at the boundary. */
function toFiscalQuota(quota: FiscalQuotaResponse): FiscalQuota {
  return {
    ...quota,
    subsidies: quota.subsidies ? quota.subsidies.filter(isSubsidyEntry) : null,
  };
}

function toFilterQuery(filters: BeneficiaryListFilters) {
  const many = <T>(values: T[] | undefined) => (values && values.length > 0 ? values : undefined);
  return {
    search: filters.search || undefined,
    status: many(filters.status),
    sector: many(filters.sector),
    incentive_type: many(filters.incentive_type),
    company_type: many(filters.company_type),
    tag: many(filters.tag),
    has_current_year_quota: filters.has_current_year_quota,
  };
}

const toSortQuery = (sort: BeneficiarySort[]) =>
  sort.map((entry) => `${entry.key}:${entry.direction}`).join(",");

const toExportFilterQuery = ({ sort: _sort, ...filters }: BeneficiaryExportParams) =>
  toFilterQuery(filters);

export class BeneficiaryRepository implements IBeneficiaryRepository {
  // --- Document helper methods (inlined from documentService) ---

  private async uploadBankCertificateDocument(
    beneficiaryUuid: string,
    bankAccountUuid: string,
    certificate: File,
  ): Promise<DocumentWithReviewResponse> {
    return unwrap(
      sdk.uploadBeneficiaryDocument({
        path: { beneficiary_uuid: beneficiaryUuid },
        body: {
          document: certificate,
          document_type: DocumentType.BANK_ACCOUNT_CERTIFICATE,
          entity_type: EntityType.BENEFICIARY_BANK_ACCOUNT,
          entity_uuid: bankAccountUuid,
          title: "Bank Account Certificate",
        },
      }),
    );
  }

  private async findBankCertificate(
    beneficiaryUuid: string,
    bankAccountUuid: string,
  ): Promise<DocumentWithReviewResponse | null> {
    const portfolio = await unwrap(
      sdk.listBeneficiaryDocuments({
        path: { beneficiary_uuid: beneficiaryUuid },
        query: { document_type: DocumentType.BANK_ACCOUNT_CERTIFICATE, limit: 500 },
      }),
    );
    const group = portfolio.from_bank_accounts.find((g) => g.bank_account_uuid === bankAccountUuid);
    return group?.documents[0] ?? null;
  }

  // --- Beneficiary creation ---

  async createBeneficiary(
    data: Parameters<IBeneficiaryRepository["createBeneficiary"]>[0],
  ): Promise<CreateBeneficiaryResponse> {
    const banking = data.banking_info;
    const beneficiary = await unwrap(
      sdk.createBeneficiary({
        body: {
          name: data.name,
          tax_id: data.tax_id,
          company_type: data.company_type,
          main_activities: data.main_activities,
          constitution_date: data.constitution_date || null,
          address: data.address || null,
          city: data.city || null,
          state: data.state || null,
          postal_code: data.postal_code || null,
          country: data.country || null,
          phone: data.phone || null,
          email: data.email || null,
          website: data.website || null,
          business_purpose: data.business_purpose || null,
          cnae_code: data.cnae_code || null,
          last_year_revenue: data.last_year_revenue || null,
          employee_count: data.employee_count ?? null,
          incentive_type: data.incentive_type || null,
          legal_representatives: [data.legal_representative],
          bank_account: banking
            ? { bank_name: banking.bank_name, iban: banking.iban, swift: banking.swift }
            : null,
        },
      }),
    );

    if (banking?.certificate) {
      const { items } = await unwrap(
        sdk.listBankAccounts({ path: { beneficiary_uuid: beneficiary.uuid } }),
      );
      const [account] = items;
      if (account) {
        await this.uploadBankCertificateDocument(
          beneficiary.uuid,
          account.uuid,
          banking.certificate,
        );
      }
    }

    return beneficiary;
  }

  // --- Metrics ---

  async getBeneficiaryMetrics(
    params?: BeneficiaryMetricsParams,
    signal?: AbortSignal,
  ): Promise<BeneficiaryMetrics> {
    const query = params && {
      search: params.search,
      status: params.status ? [params.status] : undefined,
      sector: params.sector ? [params.sector] : undefined,
    };
    return unwrap(sdk.getBeneficiaryMetrics({ query, signal }));
  }

  // --- CRUD ---

  async getBeneficiaries(
    params?: BeneficiaryListParams,
    signal?: AbortSignal,
  ): Promise<{
    beneficiaries: BeneficiaryDetail[];
    total: number;
    page: number;
    total_pages: number;
  }> {
    const { sort, sort_by, sort_direction, limit, offset, ...filters } = params ?? {};
    const query = {
      ...toFilterQuery(filters),
      sort: sort && sort.length > 0 ? toSortQuery(sort) : undefined,
      sort_by,
      sort_direction,
      limit,
      offset,
    };
    const response = await unwrap(sdk.getBeneficiaries({ query, signal }));

    const pageSize = limit || 100;
    const currentPage = Math.floor((offset || 0) / pageSize) + 1;
    const totalPages = Math.ceil((response.total_count || 0) / pageSize);

    return {
      beneficiaries: response.items,
      total: response.total_count || response.items.length,
      page: currentPage,
      total_pages: totalPages,
    };
  }

  async getBeneficiaryAggregates(
    params: BeneficiaryAggregatesParams,
    signal?: AbortSignal,
  ): Promise<BeneficiaryAggregates> {
    const { aggregates, ...filters } = params;
    const response = await unwrap(
      sdk.getBeneficiaryAggregates({
        query: { ...toFilterQuery(filters), aggregates: aggregates.join(",") },
        signal,
      }),
    );
    return response.aggregates;
  }

  async getBeneficiary(id: string, signal?: AbortSignal): Promise<BeneficiaryDetailResponse> {
    const details = await unwrap(
      sdk.getBeneficiaryDetails({ path: { beneficiary_uuid: id }, signal }),
    );
    return { ...details, fiscal_quotas: details.fiscal_quotas.map(toFiscalQuota) };
  }

  async updateBeneficiary(
    id: string,
    data: BeneficiaryUpdateRequest,
  ): Promise<CreateBeneficiaryResponse> {
    return unwrap(sdk.updateBeneficiary({ path: { beneficiary_uuid: id }, body: data }));
  }

  async deleteBeneficiary(id: string): Promise<void> {
    await unwrap(sdk.deleteBeneficiary({ path: { beneficiary_uuid: id } }));
  }

  async setBeneficiaryStatus(
    id: string,
    status: BeneficiaryStatus,
  ): Promise<CreateBeneficiaryResponse> {
    return unwrap(sdk.updateBeneficiary({ path: { beneficiary_uuid: id }, body: { status } }));
  }

  async bulkUpdateBeneficiaries(
    uuids: readonly string[],
    data: BeneficiaryUpdateRequest,
  ): Promise<BulkResult> {
    return unwrap(sdk.bulkUpdateBeneficiaries({ body: { beneficiary_uuids: [...uuids], data } }));
  }

  async bulkDeleteBeneficiaries(uuids: readonly string[]): Promise<BulkResult> {
    return unwrap(sdk.bulkDeleteBeneficiaries({ body: { beneficiary_uuids: [...uuids] } }));
  }

  // --- Fiscal Quotas ---

  async getFiscalQuotas(beneficiaryUuid: string, signal?: AbortSignal): Promise<FiscalQuota[]> {
    const response = await unwrap(
      sdk.listFiscalQuotas({ path: { beneficiary_uuid: beneficiaryUuid }, signal }),
    );
    return response.items.map(toFiscalQuota);
  }

  async createFiscalQuota(
    beneficiaryUuid: string,
    data: CreateFiscalQuotaRequest,
  ): Promise<FiscalQuota> {
    return toFiscalQuota(
      await unwrap(
        sdk.createFiscalQuota({ path: { beneficiary_uuid: beneficiaryUuid }, body: data }),
      ),
    );
  }

  async updateFiscalQuota(
    beneficiaryUuid: string,
    quotaUuid: string,
    data: UpdateFiscalQuotaRequest,
  ): Promise<FiscalQuota> {
    return toFiscalQuota(
      await unwrap(
        sdk.updateFiscalQuota({
          path: { beneficiary_uuid: beneficiaryUuid, fiscal_quota_uuid: quotaUuid },
          body: data,
        }),
      ),
    );
  }

  async deleteFiscalQuota(beneficiaryUuid: string, quotaUuid: string): Promise<void> {
    await unwrap(
      sdk.deleteFiscalQuota({
        path: { beneficiary_uuid: beneficiaryUuid, fiscal_quota_uuid: quotaUuid },
      }),
    );
  }

  async getFiscalYearSummary(
    beneficiaryUuid: string,
    fiscalYear: number,
    signal?: AbortSignal,
  ): Promise<FiscalYearSummary> {
    const summary = await unwrap(
      sdk.getFiscalYearSummary({
        path: { beneficiary_uuid: beneficiaryUuid },
        query: { fiscal_year: fiscalYear },
        signal,
      }),
    );
    return { ...summary, quotas: summary.quotas.map(toFiscalQuota) };
  }

  async getFiscalQuotaConsolidationPreview(
    beneficiaryUuid: string,
    quotaUuid: string,
    signal?: AbortSignal,
  ): Promise<ConsolidationPreview> {
    return unwrap(
      sdk.previewFiscalQuotaConsolidation({
        path: { beneficiary_uuid: beneficiaryUuid, fiscal_quota_uuid: quotaUuid },
        signal,
      }),
    );
  }

  async commitFiscalQuotaConsolidation(
    beneficiaryUuid: string,
    quotaUuid: string,
    data: ConsolidationCommitRequest,
  ): Promise<FiscalQuota> {
    return toFiscalQuota(
      await unwrap(
        sdk.commitFiscalQuotaConsolidation({
          path: { beneficiary_uuid: beneficiaryUuid, fiscal_quota_uuid: quotaUuid },
          body: data,
        }),
      ),
    );
  }

  // --- Bank Certificates ---

  async uploadBankCertificate(
    beneficiaryUuid: string,
    accountUuid: string,
    certificate: File,
  ): Promise<DocumentWithReviewResponse> {
    return this.uploadBankCertificateDocument(beneficiaryUuid, accountUuid, certificate);
  }

  async getBankCertificate(
    beneficiaryUuid: string,
    accountUuid: string,
  ): Promise<DocumentDownloadResponse> {
    const certificate = await this.findBankCertificate(beneficiaryUuid, accountUuid);
    if (!certificate) {
      throw new ApiClientError({
        message: "No certificate found for this bank account",
        code: "NOT_FOUND",
        status: 404,
      });
    }
    return unwrap(
      sdk.downloadBeneficiaryDocument({
        path: { beneficiary_uuid: beneficiaryUuid, document_uuid: certificate.document.uuid },
      }),
    );
  }

  async downloadBankCertificate(beneficiaryUuid: string, accountUuid: string): Promise<void> {
    const certificate = await this.getBankCertificate(beneficiaryUuid, accountUuid);
    await downloadFromSignedUrl(certificate.signed_url, `certificado-bancario-${accountUuid}.pdf`);
  }

  async deleteBankCertificate(beneficiaryUuid: string, accountUuid: string): Promise<void> {
    const certificate = await this.findBankCertificate(beneficiaryUuid, accountUuid);
    if (certificate) {
      await unwrap(
        sdk.deleteBeneficiaryDocument({
          path: { beneficiary_uuid: beneficiaryUuid, document_uuid: certificate.document.uuid },
        }),
      );
    }
  }

  async getBankCertificateDetails(
    beneficiaryUuid: string,
    accountUuid: string,
  ): Promise<DocumentWithReviewResponse | null> {
    return this.findBankCertificate(beneficiaryUuid, accountUuid);
  }

  // --- Legal Representatives ---

  async updateLegalRepresentative(
    beneficiaryUuid: string,
    representativeUuid: string,
    data: LegalRepUpdateRequest,
  ): Promise<void> {
    await unwrap(
      sdk.updateLegalRepresentative({
        path: { beneficiary_uuid: beneficiaryUuid, representative_uuid: representativeUuid },
        body: data,
      }),
    );
  }

  async createLegalRepresentative(
    beneficiaryUuid: string,
    data: {
      first_name: string;
      last_name: string;
      dni: string;
      position?: string | null;
      phone?: string | null;
      email?: string | null;
      is_primary?: boolean;
      is_signer?: boolean;
    },
  ): Promise<void> {
    await unwrap(
      sdk.createLegalRepresentative({
        path: { beneficiary_uuid: beneficiaryUuid },
        body: data,
      }),
    );
  }

  async deleteLegalRepresentative(
    beneficiaryUuid: string,
    representativeUuid: string,
  ): Promise<void> {
    await unwrap(
      sdk.deleteLegalRepresentative({
        path: { beneficiary_uuid: beneficiaryUuid, representative_uuid: representativeUuid },
      }),
    );
  }

  // --- Bank Accounts ---

  async createBankAccount(beneficiaryUuid: string, data: NewBankAccount): Promise<BankAccount> {
    return unwrap(
      sdk.createBankAccount({ path: { beneficiary_uuid: beneficiaryUuid }, body: data }),
    );
  }

  async deleteBankAccount(beneficiaryUuid: string, accountUuid: string): Promise<void> {
    await unwrap(
      sdk.deleteBankAccount({
        path: { beneficiary_uuid: beneficiaryUuid, bank_account_uuid: accountUuid },
      }),
    );
  }

  async updateBankAccount(
    beneficiaryUuid: string,
    accountUuid: string,
    data: BankAccountUpdateRequest,
  ): Promise<BankAccount> {
    return unwrap(
      sdk.updateBankAccount({
        path: { beneficiary_uuid: beneficiaryUuid, bank_account_uuid: accountUuid },
        body: data,
      }),
    );
  }

  // --- Data export (preview + multi-format download) ---

  async previewExport(
    params: BeneficiaryExportParams,
    signal?: AbortSignal,
  ): Promise<ExportPreview> {
    return unwrap(sdk.previewBeneficiariesExport({ query: toExportFilterQuery(params), signal }));
  }

  async downloadExport(
    params: BeneficiaryExportParams,
    options: ExportDownloadOptions,
  ): Promise<void> {
    const { blob, filename } = await unwrapBlobWithFilename(
      sdk.exportBeneficiaries,
      {
        query: {
          ...toExportFilterQuery(params),
          format: options.format,
          columns: options.columns.length > 0 ? options.columns.join(",") : undefined,
          sort: params.sort && params.sort.length > 0 ? toSortQuery(params.sort) : undefined,
          sort_by: options.sort_by as BeneficiarySortField | undefined,
          sort_direction: options.sort_direction,
        },
      },
      `beneficiaries.${options.format}`,
    );
    downloadBlob(blob, filename);
  }

  // --- Document operations ---

  async uploadDocument(
    beneficiaryUuid: string,
    file: File,
    documentType: DocumentType,
    entityType: EntityType,
    entityUuid: string,
    title?: string,
    options?: {
      fiscalQuotaUuid?: string | null;
      shareWithInvestors?: boolean;
    },
  ): Promise<DocumentWithReviewResponse> {
    return unwrap(
      sdk.uploadBeneficiaryDocument({
        path: { beneficiary_uuid: beneficiaryUuid },
        body: {
          document: file,
          document_type: documentType,
          entity_type: entityType,
          entity_uuid: entityUuid,
          title: title || undefined,
          fiscal_quota_uuid: options?.fiscalQuotaUuid || undefined,
          share_with_investors: options?.shareWithInvestors || undefined,
        },
      }),
    );
  }

  async listDocuments(
    beneficiaryUuid: string,
    params?: ListDocumentsParams,
    signal?: AbortSignal,
  ): Promise<BeneficiaryPortfolioResponse> {
    return unwrap(
      sdk.listBeneficiaryDocuments({
        path: { beneficiary_uuid: beneficiaryUuid },
        query: params,
        signal,
      }),
    );
  }

  async listDocumentsByFiscalQuota(
    beneficiaryUuid: string,
    params?: {
      document_type?: DocumentType;
      status?: "active" | "archived";
      review_status?: "pending" | "approved" | "rejected";
      search?: string;
      per_group_limit?: number;
    },
    signal?: AbortSignal,
  ): Promise<BeneficiaryDocumentsByFiscalQuotaResponse> {
    return unwrap(
      sdk.listBeneficiaryDocumentsByFiscalQuota({
        path: { beneficiary_uuid: beneficiaryUuid },
        query: params,
        signal,
      }),
    );
  }

  async getDocument(
    beneficiaryUuid: string,
    documentUuid: string,
  ): Promise<DocumentWithReviewResponse> {
    return unwrap(
      sdk.getBeneficiaryDocument({
        path: { beneficiary_uuid: beneficiaryUuid, document_uuid: documentUuid },
      }),
    );
  }

  async getDocumentDownloadUrl(
    beneficiaryUuid: string,
    documentUuid: string,
  ): Promise<DocumentDownloadResponse> {
    return unwrap(
      sdk.downloadBeneficiaryDocument({
        path: { beneficiary_uuid: beneficiaryUuid, document_uuid: documentUuid },
      }),
    );
  }

  async deleteDocument(beneficiaryUuid: string, documentUuid: string): Promise<void> {
    await unwrap(
      sdk.deleteBeneficiaryDocument({
        path: { beneficiary_uuid: beneficiaryUuid, document_uuid: documentUuid },
      }),
    );
  }

  async reviewDocument(
    beneficiaryUuid: string,
    documentUuid: string,
    review: ReviewUpdateRequest,
  ): Promise<DocumentWithReviewResponse> {
    return unwrap(
      sdk.reviewBeneficiaryDocument({
        path: { beneficiary_uuid: beneficiaryUuid, document_uuid: documentUuid },
        body: review,
      }),
    );
  }

  async updateDocument(
    beneficiaryUuid: string,
    documentUuid: string,
    data: DocumentUpdateRequest,
  ): Promise<DocumentWithReviewResponse> {
    return unwrap(
      sdk.updateBeneficiaryDocument({
        path: { beneficiary_uuid: beneficiaryUuid, document_uuid: documentUuid },
        body: data,
      }),
    );
  }
}
